import { ITenantConcernsHook } from '@models';
import { ConcernService } from '@services';
import { useStore } from '@stores';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export const useTenantConcerns = (): ITenantConcernsHook => {
  const { concernStore } = useStore();
  const {
    loading,
    skipCount,
    totalCount,
    currentPage,
    maxResultCount,
    tenantConcerns,
    handlePageChange,
  } = concernStore;

  const [name, setName] = useState<string | undefined>();
  const [categoryName, setCategoryName] = useState<string | undefined>();
  const [serviceName, setServiceName] = useState<string | undefined>();
  const [isActivated, setIsActivated] = useState<boolean | undefined>();

  useEffect(() => {
    return () => concernStore.resetTenantConcerns();
  }, [concernStore]);

  useEffect(() => {
    concernStore.getAllTenantConcerns({
      name,
      serviceName,
      categoryName,
      isActivated,
      skipCount,
      maxResultCount,
    });
    return () => concernStore.clearTenantConcerns();
  }, [concernStore, maxResultCount, skipCount, name, categoryName, serviceName, isActivated]);

  const handleNameSearch = debounce((text: string) => {
    setName(text);
    concernStore.setSkipCount(0);
    concernStore.setCurrentPage(1);
  }, 500);

  const handleCategorySearch = debounce((text: string) => {
    setCategoryName(text);
    concernStore.setSkipCount(0);
    concernStore.setCurrentPage(1);
  }, 500);

  const handleServiceSearch = debounce((text: string) => {
    setServiceName(text);
    concernStore.setSkipCount(0);
    concernStore.setCurrentPage(1);
  }, 500);

  const handleIsActivatedChange = (isActivated: boolean | undefined) => {
    setIsActivated(isActivated);
    concernStore.setSkipCount(0);
    concernStore.setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await ConcernService.getAllTenantConcerns({
      name,
      serviceName,
      categoryName,
      isActivated,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [name, serviceName, categoryName, isActivated, totalCount]);

  return {
    loading,
    dataSource: Array.from(tenantConcerns.values()),
    totalCount,
    currentPage,
    handlePageChange,
    handleNameSearch,
    handleCategorySearch,
    handleServiceSearch,
    handleIsActivatedChange,
    fetchAllForExport,
  };
};
